
import { useState, useEffect, useRef } from "react"
import { Clock, CheckCircle, MapPin, AlertCircle, Camera, RefreshCw, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useCreateTimesheetRecord, useTimesheetRecords, RecordType } from "@/hooks/useTimesheetRecords"
import { useAuth } from "@/hooks/useAuth"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { loadFaceApiModels, getFaceDescriptor, getFaceDescriptorFromUrl, compareFaces, calculateEAR } from "@/utils/faceValidation"
import * as faceapi from '@vladmandic/face-api'

export const TimeRegistration = () => {
  const [horaAtual, setHoraAtual] = useState(new Date())
  const [location, setLocation] = useState<{ latitude: number; longitude: number; accuracy?: number; timestamp?: number } | null>(null)
  const [locationStatus, setLocationStatus] = useState<'requesting' | 'granted' | 'denied' | 'unavailable'>('requesting')
  const [address, setAddress] = useState<string>("")
  const [capturedPhoto, setCapturedPhoto] = useState<File | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user') // 'user' = frontal, 'environment' = traseira
  const [securityValidations, setSecurityValidations] = useState({
    timeValid: true,
    locationValid: true,
    photoRequired: true
  })
  const [locationHistory, setLocationHistory] = useState<Array<{coords: {latitude: number, longitude: number}, timestamp: number}>>([])
  
  // Estados para Validação Facial com IA
  const [faceApiLoaded, setFaceApiLoaded] = useState(false)
  const [loadingModels, setLoadingModels] = useState(false)
  const [referencePhotoUrl, setReferencePhotoUrl] = useState<string | null>(null)
  const [referenceDescriptor, setReferenceDescriptor] = useState<Float32Array | null>(null)
  const [livenessConfirmed, setLivenessConfirmed] = useState(false)
  const [faceMatchScore, setFaceMatchScore] = useState<number | null>(null)
  const [livenessMessage, setLivenessMessage] = useState<string>("")
  const [isFaceValidating, setIsFaceValidating] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  

  const { profile } = useAuth()
  const { mutate: createRecord, isPending: isCreating } = useCreateTimesheetRecord()
  const { data: todayRecords } = useTimesheetRecords(profile?.id, new Date().toISOString().split('T')[0])

  // Carregar foto de referência do colaborador e carregar modelos da face-api
  useEffect(() => {
    const fetchReferencePhoto = async () => {
      if (!profile?.id) return
      try {
        setLoadingModels(true)
        const { data, error } = await supabase
          .from("staff_members")
          .select("photo_url")
          .eq("user_profile_id", profile.id)
          .maybeSingle()

        if (error) {
          console.error("Erro ao carregar foto do colaborador:", error)
          return
        }

        // Carregar modelos de qualquer forma
        await loadFaceApiModels()
        setFaceApiLoaded(true)

        if (data?.photo_url) {
          setReferencePhotoUrl(data.photo_url)
          console.log("Calculando descritor da foto de referência...")
          const desc = await getFaceDescriptorFromUrl(data.photo_url)
          if (desc) {
            setReferenceDescriptor(desc)
            console.log("Descritor de referência calculado com sucesso!")
          } else {
            console.warn("Não foi possível gerar descritor facial da foto de referência.")
            toast.warning("A foto de cadastro está de má qualidade ou sem rosto nítido. O reconhecimento facial pode falhar.")
          }
        } else {
          console.log("Colaborador não possui foto de referência cadastrada.")
        }
      } catch (err) {
        console.error("Erro ao inicializar validação facial:", err)
      } finally {
        setLoadingModels(false)
      }
    }

    fetchReferencePhoto()
  }, [profile?.id])

  // Loop de detecção de piscadas em tempo real (prova de vida)
  useEffect(() => {
    let active = true
    let animationFrameId: number
    let lastEAR = 0.3

    const detectLoop = async () => {
      if (!active || !showCamera || !videoRef.current || capturedPhoto) return
      
      const video = videoRef.current
      if (video.paused || video.ended || video.readyState < 2) {
        animationFrameId = requestAnimationFrame(detectLoop)
        return
      }

      try {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.4 }))
          .withFaceLandmarks()

        if (detection) {
          const ear = calculateEAR(detection.landmarks)
          
          // Lógica de piscada: EAR abaixo de 0.20 (olho fechado) e depois acima de 0.24 (olho aberto)
          if (ear < 0.20) {
            lastEAR = ear
          } else if (ear > 0.24 && lastEAR < 0.20) {
            setLivenessConfirmed(true)
            setLivenessMessage("Prova de vida confirmada! ✓")
            lastEAR = ear
          } else {
            lastEAR = ear
          }

          if (!livenessConfirmed) {
            setLivenessMessage("Prova de vida: Pisque os olhos diante da câmera.")
          }
        } else {
          setLivenessMessage("Posicione seu rosto no centro da câmera.")
        }
      } catch (err) {
        console.error("Erro no loop de detecção facial:", err)
      }

      if (active && showCamera && !capturedPhoto) {
        setTimeout(() => {
          animationFrameId = requestAnimationFrame(detectLoop)
        }, 150)
      }
    }

    if (showCamera && faceApiLoaded && !capturedPhoto) {
      setLivenessConfirmed(false)
      setLivenessMessage("Inicializando detecção facial...")
      detectLoop()
    }

    return () => {
      active = false
      cancelAnimationFrame(animationFrameId)
    }
  }, [showCamera, faceApiLoaded, capturedPhoto, livenessConfirmed])

  useEffect(() => {
    const timer = setInterval(() => {
      setHoraAtual(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    requestLocation()
  }, [])

  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    try {
      // Usando uma API gratuita de geocoding reverso
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=pt`)
      const data = await response.json()
      
      if (data && data.address) {
        const address = data.address
        let formattedAddress = ""
        
        // Montar endereço em português
        if (address.road) formattedAddress += address.road
        if (address.house_number) formattedAddress += `, ${address.house_number}`
        if (address.neighbourhood || address.suburb) formattedAddress += `, ${address.neighbourhood || address.suburb}`
        if (address.city || address.town || address.village) formattedAddress += `, ${address.city || address.town || address.village}`
        if (address.state) formattedAddress += ` - ${address.state}`
        
        if (formattedAddress) {
          setAddress(formattedAddress)
          return formattedAddress
        }
      }
      
      // Fallback para coordenadas se não conseguir o endereço
      const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      setAddress(fallback)
      return fallback
    } catch (error) {
      console.error('Erro ao obter endereço:', error)
      const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      setAddress(fallback)
      return fallback
    }
  }

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable')
      toast.error("Geolocalização não está disponível neste dispositivo")
      return
    }

    setLocationStatus('requesting')
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        }
        
        // Validações de segurança
        const currentTime = Date.now()
        const isTimeValid = Math.abs(currentTime - position.timestamp) < 30000 // 30 segundos
        const isLocationAccurate = position.coords.accuracy < 100 // Menos de 100 metros de erro
        
        // Verificar se a localização mudou muito rapidamente (detecção de spoofing)
        const isLocationValid = validateLocationMovement(coords, currentTime)
        
        setLocation(coords)
        setLocationHistory(prev => [...prev.slice(-4), {coords, timestamp: currentTime}])
        setSecurityValidations(prev => ({
          ...prev,
          timeValid: isTimeValid,
          locationValid: isLocationValid && isLocationAccurate
        }))
        
        setLocationStatus('granted')
        
        try {
          const fullAddress = await getAddressFromCoordinates(coords.latitude, coords.longitude)
          toast.success(`Localização obtida: ${fullAddress}`)
        } catch (error) {
          console.error('Erro ao obter endereço:', error)
          toast.success("Localização obtida com sucesso!")
        }
      },
      (error) => {
        console.error('Erro ao obter localização:', error)
        setLocationStatus('denied')
        toast.error("Permissão de localização negada. É necessário permitir o acesso à localização para registrar o ponto.")
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0 // Sempre pedir nova localização
      }
    )
  }

  const validateLocationMovement = (newCoords: {latitude: number, longitude: number}, timestamp: number) => {
    if (locationHistory.length === 0) return true
    
    const lastLocation = locationHistory[locationHistory.length - 1]
    const timeDiff = (timestamp - lastLocation.timestamp) / 1000 // em segundos
    
    if (timeDiff < 10) return true // Muito pouco tempo para validar
    
    // Calcular distância aproximada em metros
    const R = 6371e3 // Raio da Terra em metros
    const φ1 = lastLocation.coords.latitude * Math.PI/180
    const φ2 = newCoords.latitude * Math.PI/180
    const Δφ = (newCoords.latitude - lastLocation.coords.latitude) * Math.PI/180
    const Δλ = (newCoords.longitude - lastLocation.coords.longitude) * Math.PI/180
    
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    const distance = R * c
    
    // Velocidade máxima razoável: 60 km/h = ~17 m/s
    const maxSpeed = 17 // metros por segundo
    const maxDistance = maxSpeed * timeDiff
    
    return distance <= maxDistance
  }

  const getNextRecordType = (): RecordType => {
    if (!todayRecords || todayRecords.length === 0) {
      return "entrada"
    }

    const sortedRecords = [...todayRecords].sort((a, b) => 
      new Date(a.record_time).getTime() - new Date(b.record_time).getTime()
    )

    const lastRecord = sortedRecords[sortedRecords.length - 1]

    switch (lastRecord.record_type) {
      case "entrada":
        return "saida_almoco"
      case "saida_almoco":
        return "volta_almoco"
      case "volta_almoco":
        return "saida"
      case "saida":
        return "entrada"
      default:
        return "entrada"
    }
  }

  const getNextRecordText = (): string => {
    const nextType = getNextRecordType()
    switch (nextType) {
      case "entrada":
        return "Registrar Entrada"
      case "saida_almoco":
        return "Saída para Almoço"
      case "volta_almoco":
        return "Volta do Almoço"
      case "saida":
        return "Registrar Saída"
      default:
        return "Bater Ponto"
    }
  }

  // Funções da câmera
  const startCamera = async () => {
    console.log("Tentando iniciar câmera...")
    
    // Garantir que os modelos de IA estão carregados
    if (!faceApiLoaded) {
      try {
        setLoadingModels(true)
        await loadFaceApiModels()
        setFaceApiLoaded(true)
      } catch (err) {
        toast.error("Erro ao carregar os modelos de IA para validação facial.")
        return
      } finally {
        setLoadingModels(false)
      }
    }

    // Verificar se MediaDevices é suportado
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error("MediaDevices não suportado neste navegador")
      toast.error("Câmera não é suportada neste navegador.")
      return
    }

    try {
      setShowCamera(true) // Mostrar a interface da câmera primeiro
      console.log("Solicitando permissão de câmera...")
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: facingMode, // Usa o estado atual (user = frontal, environment = traseira)
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      })
      
      console.log("Câmera obtida com sucesso:", stream)
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        console.log("Stream aplicado ao vídeo")
      }
      
      toast.success("Câmera ativada! Posicione-se e clique em Capturar.")
      
    } catch (error) {
      console.error('Erro ao acessar câmera:', error)
      setShowCamera(false) // Esconder a interface se falhou
      
      // Mensagens específicas por tipo de erro
      if (error.name === 'NotAllowedError') {
        toast.error("Permissão de câmera negada. Por favor, permita o acesso à câmera e tente novamente.")
      } else if (error.name === 'NotFoundError') {
        toast.error("Nenhuma câmera encontrada neste dispositivo.")
      } else if (error.name === 'NotSupportedError') {
        toast.error("Câmera não é suportada neste navegador.")
      } else {
        toast.error(`Erro ao acessar a câmera: ${error.message}`)
      }
    }
  }

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks()
      tracks.forEach(track => track.stop())
    }
    setShowCamera(false)
  }

  const switchCamera = async () => {
    // Parar câmera atual
    stopCamera()
    
    // Trocar o modo
    const newMode = facingMode === 'user' ? 'environment' : 'user'
    setFacingMode(newMode)
    
    // Reiniciar com nova câmera
    setTimeout(() => {
      startCamera()
    }, 100)
    
    toast.success(`Câmera ${newMode === 'user' ? 'frontal' : 'traseira'} ativada`)
  }

  const capturePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current
      const video = videoRef.current
      
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0)
        
        // Se temos uma foto de referência, vamos validar
        if (referenceDescriptor) {
          setIsFaceValidating(true)
          toast.info("Analisando biometria facial...")
          
          try {
            const currentDescriptor = await getFaceDescriptor(canvas)
            
            if (!currentDescriptor) {
              toast.error("Rosto não detectado na imagem. Centralize seu rosto e tente novamente.")
              setIsFaceValidating(false)
              return
            }
            
            const matchResult = compareFaces(referenceDescriptor, currentDescriptor)
            console.log("Resultado do Face Match:", matchResult)
            
            if (!matchResult.isMatch) {
              toast.error(`Divergência facial detectada! Rosto incompatível com a foto de cadastro. (Confiança: ${matchResult.confidence}%)`)
              setIsFaceValidating(false)
              return
            }
            
            setFaceMatchScore(matchResult.confidence)
            toast.success(`Identidade confirmada! (Compatibilidade: ${matchResult.confidence}%)`)
          } catch (err) {
            console.error("Erro durante o Face Match:", err)
            toast.error("Erro ao analisar a foto. Tente novamente.")
            setIsFaceValidating(false)
            return
          } finally {
            setIsFaceValidating(false)
          }
        } else if (faceApiLoaded) {
          // Sem foto de referência, mas faremos verificação básica de presença de rosto
          const currentDescriptor = await getFaceDescriptor(canvas)
          if (!currentDescriptor) {
            toast.error("Rosto não detectado na imagem. Centralize seu rosto e tente novamente.")
            return
          }
        }

        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `ponto-${Date.now()}.jpg`, { type: 'image/jpeg' })
            setCapturedPhoto(file)
            setSecurityValidations(prev => ({ ...prev, photoRequired: false }))
            stopCamera()
            toast.success("Foto capturada com sucesso!")
          }
        }, 'image/jpeg', 0.8)
      }
    }
  }


  const uploadPhoto = async (file: File): Promise<string | null> => {
    try {
      setIsUploadingPhoto(true)
      const fileExt = file.name.split('.').pop()
      const fileName = `${profile?.id}-${Date.now()}.${fileExt}`
      const filePath = `${profile?.id}/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('timesheet-photos')
        .upload(filePath, file)

      if (uploadError) {
        console.error('Erro no upload:', uploadError)
        return null
      }

      const { data } = supabase.storage
        .from('timesheet-photos')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error)
      return null
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  const registrarPonto = async () => {
    if (!profile?.id) {
      toast.error("Usuário não autenticado")
      return
    }

    // Validações de segurança
    if (locationStatus !== 'granted' || !location) {
      toast.error("É necessário permitir o acesso à localização para registrar o ponto")
      requestLocation()
      return
    }

    if (!capturedPhoto) {
      toast.error("É obrigatório tirar uma foto para registrar o ponto")
      return
    }

    if (!securityValidations.timeValid) {
      toast.error("Timestamp inválido detectado. Tente novamente.")
      return
    }

    if (!securityValidations.locationValid) {
      toast.error("Localização suspeita detectada. Verifique sua localização e tente novamente.")
      return
    }

    if (faceApiLoaded && !livenessConfirmed) {
      toast.error("É necessário confirmar a prova de vida piscando os olhos durante a captura.")
      return
    }

    try {
      // Upload da foto
      const photoUrl = await uploadPhoto(capturedPhoto)
      if (!photoUrl) {
        toast.error("Erro ao fazer upload da foto. Tente novamente.")
        return
      }

      const recordType = getNextRecordType()
      console.log("Registrando ponto - Usuário:", profile.id, "Tipo:", recordType, "Localização:", location)
      
      createRecord({
        record_type: recordType,
        location: address || `${location.latitude},${location.longitude}`,
        photo_url: photoUrl,
        notes: `Precisão GPS: ${location.accuracy?.toFixed(0)}m`,
        ai_validated: faceApiLoaded && referenceDescriptor !== null,
        face_match_score: faceMatchScore || null,
        liveness_detected: livenessConfirmed
      })

      // Limpar foto e biometria após registro
      setCapturedPhoto(null)
      setFaceMatchScore(null)
      setLivenessConfirmed(false)
      setSecurityValidations(prev => ({ ...prev, photoRequired: true }))
      
    } catch (error) {
      console.error('Erro ao registrar ponto:', error)
      toast.error("Erro ao registrar ponto. Tente novamente.")
    }
  }


  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-lg">
          <Clock className="h-5 w-5" />
          <span>Registrar Ponto</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-center">
          <div className="text-3xl md:text-4xl font-mono font-bold text-blue-600 dark:text-blue-400">
            {horaAtual.toLocaleTimeString()}
          </div>
          <div className="text-sm md:text-base text-gray-600 dark:text-gray-400">
            {horaAtual.toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              day: 'numeric',
              month: 'short'
            })}
          </div>
        </div>

        <div className="space-y-3">
          {/* Status da Localização */}
          <div className="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
            {locationStatus === 'requesting' && (
              <div className="flex items-center space-x-2 text-yellow-600 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Solicitando localização...</span>
              </div>
            )}
            {locationStatus === 'granted' && (
              <div className="flex flex-col items-center space-y-1 text-green-600">
                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>Localização obtida</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={requestLocation}
                    className="ml-2 h-7 px-2 text-xs"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Atualizar
                  </Button>
                </div>
                {address && (
                  <div className="text-xs text-center text-gray-600 dark:text-gray-400 max-w-xs">
                    {address}
                  </div>
                )}
              </div>
            )}
            {locationStatus === 'denied' && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Localização negada</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={requestLocation}
                  className="ml-2 h-7 px-2 text-xs"
                >
                  Tentar novamente
                </Button>
              </div>
            )}
            {locationStatus === 'unavailable' && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Localização indisponível</span>
              </div>
            )}
          </div>

          {/* Validações de Segurança - Compacto */}
          <div className="flex items-center justify-center space-x-3 p-2 rounded-lg bg-blue-50 dark:bg-blue-950">
            <Shield className="h-3 w-3 text-blue-600" />
            <div className="flex items-center space-x-2 text-xs">
              <div className={`flex items-center space-x-1 ${securityValidations.timeValid ? 'text-green-600' : 'text-red-600'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${securityValidations.timeValid ? 'bg-green-500' : 'bg-red-500'}`} />
                <span>Tempo</span>
              </div>
              <div className={`flex items-center space-x-1 ${securityValidations.locationValid ? 'text-green-600' : 'text-red-600'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${securityValidations.locationValid ? 'bg-green-500' : 'bg-red-500'}`} />
                <span>Local</span>
              </div>
              <div className={`flex items-center space-x-1 ${!securityValidations.photoRequired ? 'text-green-600' : 'text-orange-600'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${!securityValidations.photoRequired ? 'bg-green-500' : 'bg-orange-500'}`} />
                <span>Foto</span>
              </div>
            </div>
          </div>

          {/* Captura de Foto - Compacto */}
          <div className="space-y-2">
            <div className="text-center">
              <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                📸 Foto Obrigatória com Validação IA
              </h3>
              
              {!capturedPhoto && !showCamera && (
                <Button 
                  onClick={startCamera} 
                  variant="outline" 
                  className="w-full h-9 text-sm"
                  disabled={loadingModels || isFaceValidating}
                >
                  {loadingModels ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Iniciando IA...
                    </>
                  ) : (
                    <>
                      <Camera className="h-3 w-3 mr-2" />
                      Tirar Foto
                    </>
                  )}
                </Button>
              )}

              {showCamera && (
                <div className="space-y-2">
                  <div className="relative w-full max-w-sm mx-auto rounded-lg overflow-hidden border-2 border-dashed border-blue-400">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full"
                    />
                    {/* Visual overlay for alignment */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-64 rounded-[50%/40%] border-2 border-white/40 border-dashed" />
                    </div>
                  </div>

                  {/* Liveness detection feedback */}
                  <div className="p-2 rounded bg-gray-50 dark:bg-gray-800 text-xs font-medium flex flex-col items-center space-y-1">
                    <span className={livenessConfirmed ? "text-green-600 font-semibold animate-pulse" : "text-blue-600"}>
                      {livenessMessage}
                    </span>
                    {referencePhotoUrl && !referenceDescriptor && (
                      <span className="text-[10px] text-amber-500">
                        Processando biometria de referência...
                      </span>
                    )}
                    {!referencePhotoUrl && (
                      <span className="text-[10px] text-orange-500 font-semibold">
                        Aviso: Sem foto de cadastro de referência para verificação facial.
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col items-center space-y-2">
                    <div className="flex space-x-2 justify-center">
                      <Button 
                        onClick={capturePhoto} 
                        size="sm"
                        disabled={isFaceValidating}
                      >
                        {isFaceValidating ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                            Analisando...
                          </>
                        ) : (
                          <>
                            <Camera className="h-3 w-3 mr-2" />
                            Capturar
                          </>
                        )}
                      </Button>
                      <Button onClick={switchCamera} variant="outline" size="sm" disabled={isFaceValidating}>
                        <RefreshCw className="h-3 w-3 mr-2" />
                        Trocar
                      </Button>
                      <Button onClick={stopCamera} variant="outline" size="sm" disabled={isFaceValidating}>
                        Cancelar
                      </Button>
                    </div>
                    <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                      {facingMode === 'user' ? '📱 Câmera Frontal' : '📸 Câmera Traseira'}
                    </p>
                  </div>
                </div>
              )}

              {capturedPhoto && (
                <div className="space-y-2">
                  <div className="text-xs text-green-600 font-medium flex items-center justify-center space-x-1">
                    <span>✓ Foto capturada</span>
                    {livenessConfirmed && (
                      <span className="text-[10px] bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded text-green-700 font-semibold">
                        Prova de Vida OK
                      </span>
                    )}
                  </div>
                  {faceMatchScore !== null && (
                    <div className="text-xs text-blue-600 font-semibold bg-blue-50 dark:bg-blue-900/30 p-1.5 rounded inline-block">
                      Compatibilidade Facial: {faceMatchScore}%
                    </div>
                  )}
                  <div className="pt-1">
                    <Button 
                      onClick={() => {
                        setCapturedPhoto(null)
                        setFaceMatchScore(null)
                        setLivenessConfirmed(false)
                        setSecurityValidations(prev => ({ ...prev, photoRequired: true }))
                      }} 
                      variant="outline" 
                      size="sm"
                      className="h-8 text-xs"
                    >
                      Trocar Foto
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Botão Central de Ponto - Compacto */}
          <div className="flex flex-col items-center space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Próximo: <span className="font-semibold">{getNextRecordText()}</span>
            </p>
            
            <button
              onClick={registrarPonto}
              disabled={
                isCreating || 
                isUploadingPhoto || 
                locationStatus !== 'granted' || 
                !capturedPhoto || 
                !securityValidations.timeValid || 
                !securityValidations.locationValid ||
                (faceApiLoaded && !livenessConfirmed) ||
                isFaceValidating
              }
              className="relative w-24 h-24 md:w-28 md:h-28 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
            >
              {(isCreating || isUploadingPhoto) ? (
                <Loader2 className="h-6 w-6 md:h-7 md:w-7 text-white animate-spin mx-auto" />
              ) : (
                <>
                  <CheckCircle className="h-6 w-6 md:h-7 md:w-7 text-white mx-auto mb-1" />
                  <span className="text-white font-semibold text-xs md:text-sm">
                    Bater Ponto
                  </span>
                </>
              )}
            </button>
            
            {(!capturedPhoto || !securityValidations.timeValid || !securityValidations.locationValid || (faceApiLoaded && !livenessConfirmed)) && (
              <div className="text-[10px] md:text-xs text-red-600 text-center max-w-48 whitespace-pre-line">
                {!capturedPhoto && "• Foto obrigatória\n"}
                {!securityValidations.timeValid && "• Timestamp inválido\n"}
                {!securityValidations.locationValid && "• Localização suspeita\n"}
                {faceApiLoaded && !livenessConfirmed && "• Prova de vida pendente (pisque os olhos)"}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
